import { useCallback, useMemo } from 'react';

import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import httpInstances from '@/api/axiosInstance';
import { dashboardQueryKeys } from '@/features/Dashboard/api/queries';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { normalizeTabEntry } from '@/features/Dashboard/lib/transformers';
import { buildCurrentTabPayload } from '@/features/Dashboard/pages/Edit/utils/payload';
import { DashboardTab } from '@/features/Dashboard/types';
import { useDashboard } from '@/hooks/api/dashboard';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';
import { getUniqueName } from '@/utils/getUniqueName';

type MoveTabToDashboardMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  tabId: string;
  tabName: string;
  disableMove?: boolean;
  onClose: () => void;
  onMoved?: () => void;
};

const menuItemStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  p: 1,
  pr: 2,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  }
} as const;

const disabledMenuItemStyles = {
  ...menuItemStyles,
  cursor: 'not-allowed',
  opacity: 0.6,
  '&:hover': {
    backgroundColor: 'inherit'
  }
} as const;

const MoveTabToDashboardMenu = ({
  anchorEl,
  disableMove,
  onClose,
  onMoved,
  open,
  tabId,
  tabName
}: MoveTabToDashboardMenuProps) => {
  const { data: dashboards } = useDashboard();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();
  const queryClient = useQueryClient();
  const {
    dashboardName: draftDashboardName,
    initialDashboardName,
    getAllTabDrafts,
    setTabDrafts
  } = useEditDashboardDraft();

  const effectiveCurrentDashboard = draftDashboardName || initialDashboardName;

  const dashboardList = useMemo(() => {
    const names = new Set(Object.keys(dashboards || {}));
    // Exclude the dashboard we are currently editing
    names.delete(effectiveCurrentDashboard);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [dashboards, effectiveCurrentDashboard]);

  const { mutate: moveTabToDashboard, isPending } = useMutation({
    mutationFn: async ({
      dashboardName,
      payload
    }: {
      dashboardName: string;
      payload: DashboardTab;
    }) => {
      return httpInstances.axiosInternalInstance.post(
        `/config/dashboard/${encodeURIComponent(dashboardName)}`,
        payload
      );
    },
    onSuccess: async () => {
      showSuccessSnackbar('Page moved to dashboard!');
      // Remove local draft entry for moved tab
      setTabDrafts(prev => {
        const { [tabId]: _removed, ...rest } = prev;
        return rest;
      });
      onMoved?.();
      onClose();
      await queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });
    },
    onError: () => {
      showErrorSnackbar("Couldn't move page. Please try again.");
    }
  });

  const handleSelectDashboard = useCallback(
    (targetDashboard: string) => {
      if (disableMove) {
        showErrorSnackbar('Add another page before moving this one.');
        return;
      }

      if (isPending) return;

      const drafts = getAllTabDrafts();
      const tabDraft = drafts[tabId];
      if (!tabDraft) {
        showErrorSnackbar('Tab content is missing. Please try again.');
        return;
      }

      const targetTabs = dashboards?.[targetDashboard] ?? {};
      const normalizedTabs: DashboardTab = {};
      const usedIds = new Set<string>();

      Object.entries(targetTabs).forEach(([name, value]) => {
        const normalized = normalizeTabEntry(name, value);
        normalizedTabs[name] = normalized;
        if (normalized.id) {
          usedIds.add(normalized.id);
        }
      });

      const uniqueName = getUniqueName(Object.keys(normalizedTabs), tabName);
      const newTabId = generateUniqueUUID(usedIds);
      normalizedTabs[uniqueName] = {
        id: newTabId,
        content: buildCurrentTabPayload(tabDraft.tiles, tabDraft.marketTabApps)
      };

      moveTabToDashboard({
        dashboardName: targetDashboard,
        payload: normalizedTabs
      });
    },
    [
      dashboards,
      disableMove,
      getAllTabDrafts,
      isPending,
      moveTabToDashboard,
      showErrorSnackbar,
      tabId,
      tabName
    ]
  );

  return (
    <Popper
      anchorEl={anchorEl}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4]
          }
        }
      ]}
      open={open}
      placement='bottom-start'
      sx={{ zIndex: 10000 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          sx={{
            backgroundColor: 'secondary.main',
            minWidth: 220
          }}
        >
          {dashboardList.length === 0 ? (
            <Box sx={disabledMenuItemStyles}>No other dashboards</Box>
          ) : (
            dashboardList.map(name => (
              <Box
                key={name}
                sx={disableMove ? disabledMenuItemStyles : menuItemStyles}
                onClick={() => {
                  handleSelectDashboard(name);
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  {name}
                </Typography>
              </Box>
            ))
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default MoveTabToDashboardMenu;
